import { apiSlice } from "./apiSlice";
import { TWITCH_URL } from "../constants";

export const twitchApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    validate: builder.query({
      query: () => ({
        url: `${TWITCH_URL}/user/integration/validate`,
      }),
    }),
    logoutTwitchUser: builder.mutation({
      query: () => ({
        url: `${TWITCH_URL}/logout`,
        method: "POST",
      }),
    }),
    twitchCallback: builder.mutation({
      query: (data) => ({
        url: `${TWITCH_URL}/callback`,
        method: "POST",
        body: data,
      }),
    }),
    getProfile: builder.query({
      query: () => ({
        url: `${TWITCH_URL}/user/me`,
      }),
    }),
    getUsers: builder.query({
      query: ({
        pageNumber,
        postsPerPage,
        searchTerm,
        selectedRole,
        blockedFilter,
        deletedFilter,
      }) => ({
        url: `${TWITCH_URL}/users`,
        params: {
          pageNumber,
          postsPerPage,
          searchTerm,
          selectedRole,
          blockedFilter,
          deletedFilter,
        },
      }),
      providesTags: ["User"],
      keepUnusedDataFor: 30,
    }),
    getUserDetails: builder.query({
      query: (id) => ({
        url: `${TWITCH_URL}/user/${id}`,
      }),
      providesTags: ["User"],
    }),
    updateUser: builder.mutation({
      query: (data) => ({
        url: `${TWITCH_URL}/user/${data._id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (data) => ({
        url: `${TWITCH_URL}/user/${data._id}`,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["User"],
    }),
    updateUserProfile: builder.mutation({
      query: (data) => ({
        url: `${TWITCH_URL}/user/profile`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["User", { type: "Comments", id: "LIST" }],
    }),
    updateVipUserProfile: builder.mutation({
      query: (data) => ({
        url: `${TWITCH_URL}/vip-user/profile`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["User", { type: "Comments", id: "LIST" }],
    }),
    getUserPostLikes: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-post-likes`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["PostLikes"],
    }),
    getLoggedInUserPostLikes: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-post-likes`,
          params: { pageNumber },
        };
      },
      providesTags: ["PostLikes"],
    }),
    getUserPostBookmarks: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-post-bookmarks`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["PostBookmarks"],
    }),
    getLoggedInUserPostBookmarks: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-post-bookmarks`,
          params: { pageNumber },
        };
      },
      providesTags: ["PostBookmarks"],
    }),
    getUserGameLikes: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-game-likes`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["GameLikes"],
    }),
    getLoggedInUserGameLikes: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-game-likes`,
          params: { pageNumber },
        };
      },
      providesTags: ["GameLikes"],
    }),

    getUserGameBookmarks: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-game-bookmarks`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["GameBookmarks"],
    }),
    getLoggedInUserGameBookmarks: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-game-bookmarks`,
          params: { pageNumber },
        };
      },
      providesTags: ["GameBookmarks"],
    }),
    likeUser: builder.mutation({
      query: (userId) => ({
        url: `${TWITCH_URL}/${userId}/like`,
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "Likes" }],
    }),
    bookmarkUser: builder.mutation({
      query: (userId) => ({
        url: `${TWITCH_URL}/${userId}/bookmark`,
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "Bookmarks" }],
    }),
    getUserLikes: builder.query({
      query: (userId) => ({
        url: `${TWITCH_URL}/${userId}/user-likes`,
      }),
    }),
    getUserBookmarks: builder.query({
      query: (userId) => ({
        url: `${TWITCH_URL}/${userId}/user-bookmarks`,
      }),
    }),
    getUserUserLikes: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-user-likes`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["UserLikes"],
    }),
    getLoggedInUserUserLikes: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-user-likes`,
          params: { pageNumber },
        };
      },
      providesTags: ["UserLikes"],
    }),
    getUserUserBookmarks: builder.query({
      query: (args) => {
        const { pageNumber, userId } = args;
        return {
          url: `${TWITCH_URL}/user-user-bookmarks`,
          params: { pageNumber, userId },
        };
      },
      providesTags: ["UserBookmarks"],
    }),
    getLoggedInUserUserBookmarks: builder.query({
      query: (args) => {
        const { pageNumber } = args;
        return {
          url: `${TWITCH_URL}/logged-in-user-user-bookmarks`,
          params: { pageNumber },
        };
      },
      providesTags: ["UserBookmarks"],
    }),
    updateUserProfileBanner: builder.mutation({
      query: (formData) => {
        return {
          url: `${TWITCH_URL}/user-profile-banner`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["User"],
    }),
    deleteUserProfileBanner: builder.mutation({
      query: () => ({
        url: `${TWITCH_URL}/user-profile-banner`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
    deleteSpam: builder.mutation({
      query: (args) => {
        const { id } = args;
        return {
          url: `${TWITCH_URL}/delete-user-spam`,
          method: "DELETE",
          body: { id },
        };
      },
      invalidatesTags: ["User"],
    }),
  }),
});

export const {
  useValidateQuery,
  useLogoutTwitchUserMutation,
  useGetProfileQuery,
  useTwitchCallbackMutation,
  useGetUsersQuery,
  useGetUserDetailsQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUpdateUserProfileMutation,
  useUpdateVipUserProfileMutation,
  useGetUserPostLikesQuery,
  useGetLoggedInUserPostLikesQuery,
  useGetUserPostBookmarksQuery,
  useGetLoggedInUserPostBookmarksQuery,
  useGetUserGameLikesQuery,
  useGetLoggedInUserGameLikesQuery,
  useGetUserGameBookmarksQuery,
  useGetLoggedInUserGameBookmarksQuery,
  useLikeUserMutation,
  useBookmarkUserMutation,
  useGetUserLikesQuery,
  useGetUserBookmarksQuery,
  useGetUserUserLikesQuery,
  useGetLoggedInUserUserLikesQuery,
  useGetUserUserBookmarksQuery,
  useGetLoggedInUserUserBookmarksQuery,
  useUpdateUserProfileBannerMutation,
  useDeleteUserProfileBannerMutation,
  useDeleteSpamMutation,
} = twitchApiSlice;
